import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FormInputType } from 'services/models/form-builder/enum/form-input-type.enum';
import { FormType } from 'services/models/form-builder/enum/form-type.enum';
import { FormGroupModel } from 'services/models/form-builder/form-group.model';
import { FormItemModel } from 'services/models/form-builder/form-item.model';
import { FormSubmissionAnswerModel } from 'services/models/form-builder/form-submission-answer-model';
import { FormModel } from 'services/models/form-builder/form.model';

export interface FormBuilderItemOption {
  label: string;
  order?: number;
}

type GridValue = { [rowLabel: string]: string | string[] | null };
type NpsBand = {
  emoji: string;
  label: string;
  values: string;
  className: string;
};

@Component({
  selector: 'app-form-section-answer',
  templateUrl: './form-section-answer.component.html',
  styleUrls: ['./form-section-answer.component.scss'],
})
export class FormBuilderAnswerComponent implements OnInit, OnChanges {
  @Input() title!: string;
  @Input() group!: FormGroupModel;
  @Input() items: FormItemModel[] = [];
  @Input() formData!: FormModel;
  @Input() isSubmitting: boolean = false;
  @Input() initialValues: { [key: string]: any } = {};
  @Input() currentStep: number = 0;
  @Input() isLastGroup: boolean = false;
  @Input() readonly: boolean = false;
  @Input() showHeader: boolean = true;
  @Input() showFooter: boolean = true;
  @Input() showSubmitButton: boolean = true;
  @Input() recruitmentId?: string;
  /** Uses anonymous admission pre-signed URL (preregistration page). */
  @Input() useAdmissionFileUpload = false;
  @Output() onSubmit = new EventEmitter<{ [key: string]: any }>();
  @Output() onCancel = new EventEmitter<void>();
  @Output() onPrev = new EventEmitter<void>();

  list: FormItemModel[] = [];
  form!: FormGroup;
  collapsedItems: { [key: string]: boolean } = {};
  readonly npsValues = Array.from({ length: 11 }, (_, index) => `${index}`);
  readonly npsBands: NpsBand[] = [
    { emoji: '😞', label: 'Detratores', values: '0 a 6', className: 'is-negative' },
    { emoji: '😐', label: 'Neutros', values: '7 a 8', className: 'is-neutral' },
    { emoji: '😊', label: 'Promotores', values: '9 a 10', className: 'is-positive' },
  ];

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.buildForm();
    this.initializeCollapsedItems();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.items || changes.initialValues) {
      this.buildForm();
    }
  }

  buildForm() {
    this.form = this.fb.group({});

    this.items.forEach(item => {
      if (!item.id) {
        return;
      }

      const validators = this._buildValidators(item);
      this.form.addControl(
        item.id,
        this.fb.control(this.initialValues[item.id] || null, validators)
      );
    });
  }

  handleSubmit() {
    if (this.form.valid) {
      const payload = this._payload();

      this.onSubmit.emit(payload);
    } else {
      this.form.markAllAsTouched();
    }
  }

  goToPreviousStep() {
    if (this.currentStep === 0) {
      this.onCancel.emit();
    } else {
      this.onPrev.emit();
    }
  }

  onMultiSelectChange(id: string, value: string, checked: boolean) {
    const control = this.form.get(id);
    if (!control) {
      return;
    }

    const current = control.value || [];

    if (checked) {
      control.setValue([...current, value]);
    } else {
      control.setValue(current.filter((v: string) => v !== value));
    }
  }

  onGridRadioChange(id: string, rowLabel: string, columnLabel: string) {
    const control = this.form.get(id);
    if (!control) {
      return;
    }

    const current = (control.value || {}) as GridValue;

    control.setValue({
      ...current,
      [rowLabel]: columnLabel,
    });
    control.markAsTouched();
  }

  onGridCheckboxChange(id: string, rowLabel: string, columnLabel: string, checked: boolean) {
    const control = this.form.get(id);
    if (!control) {
      return;
    }

    const current = ((control.value || {}) as GridValue) || {};
    const selectedColumns = Array.isArray(current[rowLabel]) ? [...(current[rowLabel] as string[])] : [];
    const nextColumns = checked
      ? Array.from(new Set([...selectedColumns, columnLabel]))
      : selectedColumns.filter(value => value !== columnLabel);

    control.setValue({
      ...current,
      [rowLabel]: nextColumns,
    });
    control.markAsTouched();
  }

  isGridRadioChecked(item: FormItemModel, rowLabel: string, columnLabel: string) {
    if (!item.id) {
      return false;
    }

    const value = (this.form.get(item.id)?.value || {}) as GridValue;
    return value[rowLabel] === columnLabel;
  }

  isGridCheckboxChecked(item: FormItemModel, rowLabel: string, columnLabel: string) {
    if (!item.id) {
      return false;
    }

    const value = (this.form.get(item.id)?.value || {}) as GridValue;
    return Array.isArray(value[rowLabel]) && value[rowLabel].includes(columnLabel);
  }

  toggleCollapse(itemId: string) {
    this.collapsedItems[itemId] = !this.collapsedItems[itemId];
  }

  isExpanded(itemId: string) {
    return this.collapsedItems[itemId];
  }

  get submitButtonText() {
    if (this.formData.type === FormType.WORK_CHECK.id) {
      if (this.readonly) {
        return this.isLastGroup ? 'Fechar' : 'Próximo';
      }

      return this.isLastGroup ? 'Finalizar' : 'Próximo';
    }

    if (
      [FormType.PROBATIONARY_REVIEW_EMPLOYEE.id, FormType.PROBATIONARY_REVIEW_MANAGER.id].includes(
        this.formData.type
      )
    ) {
      return this.isLastGroup ? 'Finalizar' : 'Próximo';
    }

    return this.isLastGroup ? 'Finalizar' : 'Próximo';
  }

  initializeCollapsedItems() {
    this.items.forEach(item => {
      if (item.inputType === 'TEXTAREA' && item.id) {
        this.collapsedItems[item.id] = true;
      }
    });
  }

  getGridRows(item: FormItemModel): string[] {
    const rows = new Set<string>();

    (item.options || [])
      .slice()
      .sort((left, right) => (left.order || 0) - (right.order || 0))
      .forEach(option => {
        const rowLabel = (option.label || '').trim();
        if (rowLabel) {
          rows.add(rowLabel);
        }
      });

    return Array.from(rows);
  }

  getGridColumns(item: FormItemModel): string[] {
    const columns = new Set<string>();

    (item.options || [])
      .slice()
      .sort((left, right) => (left.order || 0) - (right.order || 0))
      .forEach(option => {
        const columnLabel = (option.legend || '').trim();
        if (columnLabel) {
          columns.add(columnLabel);
        }
      });

    return Array.from(columns);
  }

  isSimpleGrid(item: FormItemModel): boolean {
    return item.inputType === FormInputType.RADIO_OPTION_GRID.value;
  }

  isMultiGrid(item: FormItemModel): boolean {
    return item.inputType === FormInputType.RADIO_OPTION_MULTISELECT_GRID.value;
  }

  isNps(item: FormItemModel): boolean {
    return item.inputType === FormInputType.NPS.value;
  }

  selectNpsValue(itemId: string, value: string) {
    if (this.readonly) {
      return;
    }

    const control = this.form.get(itemId);
    if (!control) {
      return;
    }

    control.setValue(value);
    control.markAsTouched();
  }

  isNpsSelected(itemId: string, value: string): boolean {
    return this.form.get(itemId)?.value === value;
  }

  getNpsToneClass(value: string): string {
    const numericValue = Number(value);

    if (numericValue <= 6) {
      return 'is-negative';
    }

    if (numericValue <= 8) {
      return 'is-neutral';
    }

    return 'is-positive';
  }

  hasNpsSelection(itemId: string): boolean {
    return !!this.form.get(itemId)?.value;
  }

  getSelectedNpsValue(itemId: string): string | null {
    return this.form.get(itemId)?.value || null;
  }

  getSelectedNpsBand(itemId: string): NpsBand | null {
    const selectedValue = this.getSelectedNpsValue(itemId);
    if (!selectedValue) {
      return null;
    }

    const toneClass = this.getNpsToneClass(selectedValue);
    return this.npsBands.find(band => band.className === toneClass) || null;
  }

  isNpsBandSelected(itemId: string, className: string): boolean {
    return this.getSelectedNpsBand(itemId)?.className === className;
  }

  private _buildValidators(item: FormItemModel): ValidatorFn[] {
    if (!item.required) {
      return [];
    }

    if (this.isSimpleGrid(item) || this.isMultiGrid(item)) {
      return [this._gridRequiredValidator(item)];
    }

    return [Validators.required];
  }

  private _gridRequiredValidator(item: FormItemModel): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const rows = this.getGridRows(item);
      const value = (control.value || {}) as GridValue;

      const hasIncompleteRow = rows.some(rowLabel => {
        const rowValue = value[rowLabel];

        if (this.isSimpleGrid(item)) {
          return !rowValue;
        }

        return !Array.isArray(rowValue) || rowValue.length === 0;
      });

      return hasIncompleteRow ? { required: true } : null;
    };
  }

  private _getGridOptionId(item: FormItemModel, rowLabel: string, columnLabel: string): string | null {
    const option = (item.options || []).find(candidate => {
      const row = (candidate.label || '').trim();
      const column = (candidate.legend || '').trim();
      return row === rowLabel && column === columnLabel;
    });

    return option?.id || null;
  }

  private _payload() {
    const payload = this.form.value;
    const result: any[] = [];

    for (const [formItemId, value] of Object.entries(payload)) {
      const item = this.items.find(x => x.id === formItemId);
      if (!item) continue;

      const entry: any = { formItemId };

      if (this.isSimpleGrid(item) || this.isMultiGrid(item)) {
        const gridValue = (value || {}) as GridValue;
        const optionIds: string[] = [];
        const formattedValue = this.getGridRows(item)
          .map(rowLabel => {
            const selected = gridValue[rowLabel];
            const selectedColumns = Array.isArray(selected)
              ? selected
              : selected
              ? [selected]
              : [];

            selectedColumns.forEach(columnLabel => {
              const optionId = this._getGridOptionId(item, rowLabel, columnLabel);
              if (optionId) {
                optionIds.push(optionId);
              }
            });

            return selectedColumns.length ? `${rowLabel}: ${selectedColumns.join(', ')}` : null;
          })
          .filter(Boolean)
          .join('; ');

        entry.value = formattedValue;
        entry.formItemOptionIds = optionIds;
        result.push(entry);
        continue;
      }

      if (item.options && item.options.length > 0) {
        if (
          item.inputType === FormInputType.MULTISELECT.value ||
          item.inputType === FormInputType.RADIO_OPTION_MULTISELECT.value
        ) {
          // 🔧 Garante que o value será sempre string
          const selectedLabels = Array.isArray(value) ? value : [value];
          entry.value = selectedLabels.join(';');

          // Mapeia IDs das opções selecionadas
          entry.formItemOptionIds = item.options
            .filter(opt => selectedLabels.includes(opt.label))
            .map(opt => opt.id);
        } else {
          // Tipo não multiselect → valor simples
          entry.value = value;

          const option = item.options.find(opt => opt.label === value);
          if (option) entry.formItemOptionIds = [option.id];
        }
      } else {
        // Campo sem opções → mantém valor puro
        entry.value = value;
      }

      result.push(entry);
    }

    return result;
  }

  public get showFinishButton(): boolean {
    if (!this.showSubmitButton) {
      return false;
    }

    return !this.readonly ? !this.isSubmitting : !this.isLastGroup;
  }
}
